// Two different animations, so two constants — one value used to cover both, which stopped
// working the moment the drawer's slide and the tier slide stopped sharing a duration.
// Matches $drawer-transition (0.4s): the horizontal Tier 1<->2<->3 panel slide.
const TIER_TRANSITION_MS = 400;
// Matches $drawer-slide-duration (0.4s): the drawer's own vertical open/close. Gates the
// post-close panel reset and the header pin, so it must stay >= that slide or the panels snap
// back and the header lets go while the drawer is still moving.
const DRAWER_SLIDE_MS = 400;
const TRANSLATE_T2 = 'translateX(calc(-100% / 3))';
const TRANSLATE_T3 = 'translateX(calc(-200% / 3))';

const initMobileNav = () => {
  const panelsEl = document.getElementById('bfx-nav-panels');
  if (!panelsEl) return;

  const navDrawer = document.getElementById('NavDrawer');
  let currentCategory = null;
  let currentSubcategory = null;
  let trapContainer = navDrawer;
  let settleTimer = null;

  const tier2Panels = () => panelsEl.querySelectorAll('.bfx-nav-drawer__panel--tier2');
  const tier3Panels = () => panelsEl.querySelectorAll('.bfx-nav-drawer__panel--tier3');

  const tier1Panel = panelsEl.querySelector('.bfx-nav-drawer__panel--tier1');

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const getFocusable = (container) =>
    [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);

  const retrapFocus = (elementToFocus, container) => {
    if (!navDrawer || !window.theme?.a11y) return;
    window.theme.a11y.removeTrapFocus({ container: trapContainer, namespace: 'drawer_focus' });
    trapContainer = container ?? navDrawer;
    window.theme.a11y.trapFocus({
      container: trapContainer,
      namespace: 'drawer_focus',
      elementToFocus,
    });
  };

  const drillTo = (category) => {
    tier2Panels().forEach((panel) => {
      if (panel.dataset.category === category) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
    currentCategory = category;
    panelsEl.style.transform = TRANSLATE_T2;

    panelsEl.querySelectorAll('[data-drill-to]').forEach((btn) => {
      btn.setAttribute('aria-expanded', String(btn.dataset.drillTo === category));
    });

    setTimeout(() => {
      if (currentCategory !== category) return; // user navigated away before transition ended
      // remove Tier 1 from tab order while Tier 2 is active
      tier1Panel?.setAttribute('inert', '');
      const activePanel = panelsEl.querySelector(
        `.bfx-nav-drawer__panel--tier2[data-category="${category}"]`
      );
      const backBtn = activePanel?.querySelector('.bfx-nav-drawer__back');
      retrapFocus(backBtn, activePanel); // trap on Tier 2 panel only — inert Tier 1 els excluded
    }, TIER_TRANSITION_MS);
  };

  const goBack = () => {
    const returningTo = currentCategory;
    panelsEl.style.transform = 'translateX(0)';
    panelsEl.querySelectorAll('[data-drill-to]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    currentCategory = null;

    setTimeout(() => {
      // bail if user drilled into another panel or drawer closed before transition ended
      if (currentCategory !== null) return;
      if (!navDrawer?.classList.contains('drawer--is-open')) return;
      tier2Panels().forEach((panel) => panel.setAttribute('hidden', ''));
      // restore Tier 1 to tab order and return focus to the triggering button
      tier1Panel?.removeAttribute('inert');
      const tier1Btn = panelsEl.querySelector(`[data-drill-to="${returningTo}"]`);
      retrapFocus(tier1Btn, navDrawer); // restore trap to full drawer
    }, TIER_TRANSITION_MS);
  };

  const drillToTier3 = (subcategory) => {
    tier3Panels().forEach((panel) => {
      if (panel.dataset.subcategory === subcategory) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
    currentSubcategory = subcategory;
    panelsEl.style.transform = TRANSLATE_T3;

    panelsEl.querySelectorAll('[data-drill-to-t3]').forEach((btn) => {
      btn.setAttribute('aria-expanded', String(btn.dataset.drillToT3 === subcategory));
    });

    setTimeout(() => {
      if (currentSubcategory !== subcategory) return;
      // remove active T2 panel from tab order while T3 is active
      const activeT2 = panelsEl.querySelector(
        `.bfx-nav-drawer__panel--tier2[data-category="${currentCategory}"]`
      );
      activeT2?.setAttribute('inert', '');
      const activePanel = panelsEl.querySelector(
        `.bfx-nav-drawer__panel--tier3[data-subcategory="${subcategory}"]`
      );
      const backBtn = activePanel?.querySelector('.bfx-nav-drawer__back-t3');
      retrapFocus(backBtn, activePanel);
    }, TIER_TRANSITION_MS);
  };

  const goBackToTier2 = () => {
    const returningTo = currentSubcategory;
    panelsEl.style.transform = TRANSLATE_T2;
    panelsEl.querySelectorAll('[data-drill-to-t3]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    currentSubcategory = null;

    setTimeout(() => {
      if (currentSubcategory !== null) return;
      if (!navDrawer?.classList.contains('drawer--is-open')) return;
      tier3Panels().forEach((panel) => panel.setAttribute('hidden', ''));
      // restore active T2 panel to tab order and return focus to the triggering button
      const activeT2 = panelsEl.querySelector(
        `.bfx-nav-drawer__panel--tier2[data-category="${currentCategory}"]`
      );
      activeT2?.removeAttribute('inert');
      const t2Btn = panelsEl.querySelector(`[data-drill-to-t3="${returningTo}"]`);
      retrapFocus(t2Btn, activeT2);
    }, TIER_TRANSITION_MS);
  };

  const resetPanels = () => {
    // remove trap from current container before resetting — T2/T3 panel may still hold it
    if (window.theme?.a11y) {
      window.theme.a11y.removeTrapFocus({ container: trapContainer, namespace: 'drawer_focus' });
    }
    panelsEl.style.transition = 'none';
    panelsEl.style.transform = 'translateX(0)';
    currentCategory = null;
    currentSubcategory = null;
    trapContainer = navDrawer;
    tier1Panel?.removeAttribute('inert');
    tier2Panels().forEach((panel) => {
      panel.setAttribute('hidden', '');
      panel.removeAttribute('inert');
    });
    tier3Panels().forEach((panel) => panel.setAttribute('hidden', ''));
    panelsEl.querySelectorAll('[data-drill-to]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    panelsEl.querySelectorAll('[data-drill-to-t3]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    requestAnimationFrame(() => {
      panelsEl.style.transition = '';
    });
  };

  // Tier 1 → Tier 2
  panelsEl.querySelectorAll('[data-drill-to]').forEach((btn) => {
    btn.addEventListener('click', () => drillTo(btn.dataset.drillTo));
  });

  // Tier 2 → Tier 1
  panelsEl.querySelectorAll('.bfx-nav-drawer__back').forEach((btn) => {
    btn.addEventListener('click', goBack);
  });

  // Tier 2 → Tier 3
  panelsEl.querySelectorAll('[data-drill-to-t3]').forEach((btn) => {
    btn.addEventListener('click', () => drillToTier3(btn.dataset.drillToT3));
  });

  // Tier 3 → Tier 2
  panelsEl.querySelectorAll('.bfx-nav-drawer__back-t3').forEach((btn) => {
    btn.addEventListener('click', goBackToTier2);
  });

  // theme.a11y.trapFocus only wraps FORWARD Tab (off the last element). Supplement the
  // backward edge so Shift+Tab off the first control of the active container wraps to the
  // last instead of escaping to the page behind the drawer. Reads the live trapContainer
  // (full drawer, or an active Tier 2/3 panel) and recomputes on each keypress.
  navDrawer?.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || !e.shiftKey) return;
    if (!navDrawer.classList.contains('drawer--is-open')) return;
    const focusable = getFocusable(trapContainer ?? navDrawer);
    if (focusable.length && document.activeElement === focusable[0]) {
      e.preventDefault();
      focusable[focusable.length - 1].focus();
    }
  });

  // The drawer's own offset is CSS-only — it tucks under a header the stylesheet pins to the
  // viewport top, at a constant --header-height that Impulse keeps current on resize. Nothing here
  // measures for layout, which is the whole of E20-1293: the old code sized the drawer from the
  // header's live position, and the touch scroll lock collapses window.scrollY to 0 on open, so
  // that position changed out from under it and Impulse tore the sticky header down mid-slide.
  //
  // The one measurement left feeds the open animation. It records the gap the header is about to
  // be pinned across so it can glide rather than jump; if it were wrong the glide would start in
  // an odd place, but the header still lands pinned. Layout can't depend on it.
  const publishHeaderShift = (px) =>
    document.documentElement.style.setProperty('--bfx-header-shift', `${px}px`);

  const captureHeaderShift = () => {
    const siteHeader = document.querySelector('.site-header');
    publishHeaderShift(siteHeader ? Math.round(siteHeader.getBoundingClientRect().top) : 0);
  };

  // Sync body class + reset panels on drawer open/close
  if (navDrawer) {
    // The observer sees every class change on the drawer, not just the open/close one —
    // .is-transitioning alone fires it twice per slide. Acting on each would re-measure the header
    // after it was already pinned (recording a shift of 0 and losing the distance to travel back),
    // so only an actual change of state does anything.
    let wasOpen = navDrawer.classList.contains('drawer--is-open');

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName !== 'class') continue;
        const isOpen = navDrawer.classList.contains('drawer--is-open');
        if (isOpen === wasOpen) continue;
        wasOpen = isOpen;

        if (isOpen) {
          // Record where the header is BEFORE the pin lands, so it can travel that distance
          // instead of teleporting it (77px on the home page, under the promo banner). This is a
          // motion measurement only — it sets where the glide starts, never where it ends, which
          // is the distinction that makes it safe where the old layout measurement wasn't.
          captureHeaderShift();
          document.body.classList.add('bfx-mobile-nav-opening');
          document.body.classList.add('bfx-mobile-nav-is-open');
          document.body.classList.remove('bfx-mobile-nav-settling');
          clearTimeout(settleTimer);

          // Two frames: one for the inverted start to paint, then release it so the transition
          // has something to animate from. A single frame can be coalesced with the class add.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => document.body.classList.remove('bfx-mobile-nav-opening'));
          });
        } else {
          // Hold the pin past the drawer itself so the header can travel back to its resting
          // offset rather than dropping there in one frame — and so Impulse's sticky entrance,
          // which the released scroll lock sets running underneath, never shows. By the time this
          // clears, the header already sits where releasing the pin will leave it.
          document.body.classList.remove('bfx-mobile-nav-opening');
          document.body.classList.remove('bfx-mobile-nav-is-open');
          document.body.classList.add('bfx-mobile-nav-settling');
          clearTimeout(settleTimer);
          // Both deferred jobs ride the one timer so reopening cancels them together. Given its own
          // timeout, the panel reset still fired 400ms later against a drawer the user had reopened
          // in the meantime — snapping it back to Tier 1 and dropping the focus trap it had just
          // built — because only the settling class was ever cancelled.
          settleTimer = setTimeout(() => {
            document.body.classList.remove('bfx-mobile-nav-settling');
            resetPanels();
          }, DRAWER_SLIDE_MS);
        }
      }
    });
    observer.observe(navDrawer, { attributes: true });
  }
};

initMobileNav();
